import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  CircularProgress, 
  Tabs, 
  Tab,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Button
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as yaml from 'js-yaml';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { getGameBaseUrl, getAdminBaseUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Add type declarations for Swagger UI
declare global {
  interface Window {
    SwaggerUIBundle: any;
  }
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`swagger-tabpanel-${index}`}
      aria-labelledby={`swagger-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Docs: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adminSwaggerYaml, setAdminSwaggerYaml] = useState<any | null>(null);
  const [gameSwaggerYaml, setGameSwaggerYaml] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [token, setToken] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (user === null && authChecked) {
      navigate('/admin/login');
    } else if (user !== null) {
      setAuthChecked(true);
    }
  }, [user, authChecked]);

  useEffect(() => {
    // Get the token from localStorage
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  useEffect(() => {
    const fetchSwaggerYaml = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get the current base URLs
        const adminBaseUrl = getAdminBaseUrl();
        const gameBaseUrl = getGameBaseUrl();
        
        // Fetch both API documentations
        const [adminResponse, gameResponse] = await Promise.all([
          axios.get(`${adminBaseUrl}/api-docs`),
          axios.get(`${gameBaseUrl}/api-docs`)
        ]);
        
        // Parse the YAML strings into JavaScript objects
        try {
          // For admin API
          if (adminResponse.data) {
            try {
              const parsedAdminYaml = yaml.load(adminResponse.data);
              setAdminSwaggerYaml(parsedAdminYaml);
            } catch (adminParseError) {
              console.error('Error parsing Admin API YAML:', adminParseError);
              setAdminSwaggerYaml(null);
            }
          }
          
          // For game API
          if (gameResponse.data) {
            try {
              const parsedGameYaml = yaml.load(gameResponse.data);
              setGameSwaggerYaml(parsedGameYaml);
            } catch (gameParseError) {
              console.error('Error parsing Game API YAML:', gameParseError);
              setGameSwaggerYaml(null);
            }
          }
          
          // If both failed to parse, show an error
          if (!adminResponse.data && !gameResponse.data) {
            setError('Failed to load API documentation. Please try again later.');
          }
        } catch (parseError) {
          console.error('Error parsing YAML:', parseError);
          setError('Failed to parse API documentation. Please try again later.');
        }
      } catch (err) {
        console.error('Error fetching Swagger YAML:', err);
        setError('Failed to load API documentation. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSwaggerYaml();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(token);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            Back to Admin
          </Button>
          <Typography variant="h4" component="h1">
            API Documentation
          </Typography>
        </Box>
        
        {user && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Authentication Token
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              p: 1,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              overflow: 'auto',
              maxWidth: '100%'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {token}
              </Typography>
              <Tooltip title={copySuccess ? "Copied!" : "Copy to clipboard"}>
                <IconButton 
                  size="small"
                  color="primary" 
                  onClick={handleCopyToken}
                  aria-label="copy token"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This token is automatically included in API requests. You can copy it for testing in tools like Postman.
            </Typography>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="API documentation tabs">
                <Tab label="Admin API" id="swagger-tab-0" aria-controls="swagger-tabpanel-0" />
                <Tab label="Game API" id="swagger-tab-1" aria-controls="swagger-tabpanel-1" />
              </Tabs>
            </Box>
            <TabPanel value={tabValue} index={0}>
              {adminSwaggerYaml ? (
                <SwaggerUI 
                  spec={adminSwaggerYaml} 
                  deepLinking={true}
                />
              ) : (
                <Typography color="error">
                  Failed to load Admin API documentation. The YAML may contain errors.
                </Typography>
              )}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {gameSwaggerYaml ? (
                <SwaggerUI 
                  spec={gameSwaggerYaml} 
                  deepLinking={true}
                />
              ) : (
                <Typography color="error">
                  Failed to load Game API documentation. The YAML may contain errors.
                </Typography>
              )}
            </TabPanel>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Docs; 